import React, { useEffect, useState } from "react";
import InfinityPOSPageGridCard from "../../common/cardContainers/InfinityPOSPageGridCard";
import { APP_TITLE } from "../../../config/constant";
import { useNavigate, useOutletContext } from "react-router";
import ListComponent from "../../common/lists/ListComponent";
import { useSelector } from "react-redux";
import * as Library from "../../../utils/Library";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import { AppQueryClient } from "../../../api/queryClient";
import { defaultStatusColors, getSpanWithColor } from "../../../utils/CardUtils";
import MainWindowHeader from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";

export default function POSPage() {
    document.title = `${APP_TITLE} - POS Page`;

    // #region VARS
    // eslint-disable-next-line no-unused-vars
    const [isLoading, setIsLoading] = useState(false);

    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigate = useNavigate();
    const navigations = useSelector(state => state.navigationAccesses);

    const [listNumRows, setListNumRows] = useState(50);

    const listColumns = [
        {
            name: "POS Name",
            selector: row => row?.posName || "--"
        },
        {
            name: "Created by",
            selector: row => row?.createdBy || "--"
        },
        {
            name: "Created on",
            selector: row => row?.createdOn || "--"
        },
        {
            name: "Company",
            selector: row => row?.companyName || "--"
        },
        {
            name: "Support Email",
            selector: row => row?.supportEmail || "--"
        },
        {
            name: "Products Import",
            selector: row => getSpanWithColor(row?.productsImport ?? "--", defaultStatusColors)
        },
        {
            name: "Supplier Orders Import",
            selector: row => getSpanWithColor(row?.supplierOrdersImport ?? "--", defaultStatusColors)
        },
        {
            name: "Products Export",
            selector: row => getSpanWithColor(row?.productsExport ?? "--", defaultStatusColors)
        },
        {
            name: "Supplier Orders Export",
            selector: row => getSpanWithColor(row?.supplierOrdersExport ?? "--", defaultStatusColors)
        },
        {
            name: "Stock Import",
            selector: row => getSpanWithColor(row?.stockImport ?? "--", defaultStatusColors)
        },
        {
            name: "Prices Import",
            selector: row => getSpanWithColor(row?.pricesImport ?? "--", defaultStatusColors)
        },
        {
            name: "Customer Orders",
            selector: row => getSpanWithColor(row?.customerOrders ?? "--", defaultStatusColors)
        }
    ];

    const infiniteGridQuerykey = ["POSPageGrid"];
    const listQueryKey = ["POSPageList"];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region METHODS

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
    };

    // #endregion Methods

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);
        setupHeaders(props, onClickRefreshAction, navigations);
    }, [props?.layoutType]);
    // #endregion EFFECTS

    // #region RENDER
    return (
        <>
            <FullScreenLoader isLoading={isLoading} />

            {viewLayoutStyle === "Grid" && (
                <InfinityPOSPageGridCard
                    minColumnWidth="470px"
                    limit={10}
                    queryKey={infiniteGridQuerykey}
                    statusFromPath="status"
                    primaryButton={{
                        title: "SET AS DEFAULT",
                        onClick: async item => {
                            const res = await Library.makePostRequest(
                                "updateUserDefaultPOS",
                                { posID: item?._id },
                                false,
                                setIsLoading
                            );

                            if (res?.data?.status !== 200) return;

                            Library.showToastMessage({
                                type: "success",
                                message: "POS set as default successfully"
                            });

                            onClickRefreshAction();
                        }
                    }}
                    // onAcceptInvitation={handleAcceptInvitation}
                    // onDeclineInvitation={onOpenDeclineDialog}
                    // onDeleteInvitation={handleDeleteInvitation}
                    // requestEnabled={requestEnabled}
                    // filters={filters}
                />
            )}
            {viewLayoutStyle === "List" && (
                <ListComponent
                    title="POS"
                    //title2="(LAST 10 INVITED)"
                    columns={listColumns}
                    api={{
                        endpoint: "getPOSsData",
                        useCommonPostRequest: false,
                        formData: {
                            companyID: localStorage.getItem("currentCompanyID"),
                            limit: listNumRows
                        }
                    }}
                    pageLimit={listNumRows}
                    currentPage={1}
                    queryKey={listQueryKey}
                    actions={{
                        includeActions: true,
                        editIsVisible: true,
                        suspendIsVisible: false,
                        removeIsVisible: false,
                        extraActions: [
                            {
                                title: "SET AS DEFAULT",
                                onClick: row => {
                                    return undefined;
                                },
                                isDisabled: row => {
                                    return true;
                                }
                            },
                            {
                                title: "DETAILS",
                                onClick: row => {
                                    return undefined;
                                },
                                isDisabled: row => {
                                    return true;
                                }
                            }
                        ],
                        onClickEdit: row => {
                            navigate(`/pos/edit/${row?._id}`);
                        }
                    }}
                    includeStatus={{
                        stickyStatus: true,
                        statusFromPath: "status",
                        statusColors: defaultStatusColors
                    }}
                    //requestEnabled={requestEnabled}
                    onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                />
            )}
        </>
    );
    // #endregion RENDER
}

// #region FUNCTIONS
function setupHeaders(props, onClickRefreshAction, navigations) {
    const createMode = navigations?.navigationAccesses?.retailers?.createModeOnly;

    //const currentPOSID = localStorage.getItem("currentPOSID");
    //const routePOSEdit = `/pos/edit/${currentPOSID}`;

    props.setControlBarLeftBtns([
        //{ to: "#", disabled: true, title: "Retailers", icon: "fa-magnifying-glass", isSearchToogle: false }
    ]);
    props.setControlBarCenterBtns([
        { to: "/pos", active: true, disabled: false, title: "POS" },
        { to: "/pos/setup", active: false, disabled: false, title: "SETUP" },
        { to: "/pos/connectfields", active: false, disabled: false, title: "CONNECT FIELDS" },
        { to: "/pos/auth", active: false, disabled: false, title: "AUTH" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        { to: "/pos/create", active: false, disabled: false, title: "Add", icon: "plus" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
        //{ to: routePOSEdit, active: false, disabled: !currentPOSID, title: "Edit", icon: "edit" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    disabled: true
                }}
                secondOrganize={{
                    visible: true,
                    disabled: true
                }}
                pageFilters={{
                    useFilters: false
                }}
            />
        )
    });
}
// #endregion FUNCTIONS
